import { useMutation } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { useForm, Controller } from 'react-hook-form'
import { Check, Loader2 } from 'lucide-react'

import { request } from '@helpers/graphql'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"

const UPDATE_ORGANIZATION_MUTATION = gql`
  mutation updateOrganization($input: UpdateOrganizationInput!) {
    updateOrganization(input: $input) {
      organization {
        id
        name
        verificationStatus
        privateStudentChatsEnabled
        organizationType
        accountStatus
      }
      errors {
        message
      }
    }
  }
`

const Settings = ({ organizationId, name, verificationStatus, privateStudentChatsEnabled, organizationType, accountStatus, moderationPolicy, refetch }) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSuccess }
  } = useForm({ mode: 'onTouched', defaultValues: { name, verificationStatus, privateStudentChatsEnabled, organizationType, accountStatus: accountStatus ?? null, moderationPolicy: moderationPolicy ?? null } })

  const { mutate: updateOrganization, isLoading } = useMutation({
    mutationFn: async variables => await request(UPDATE_ORGANIZATION_MUTATION, { input: { id: organizationId, ...variables } }),
    onSuccess: () => refetch()
  })

  const onSubmit = data => {
    const cleanedData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, value === '' ? null : value])
    )
    updateOrganization(cleanedData)
  }

  return (
    <div className='w-auto px-5 py-3'>
      <form
        className='flex flex-col space-y-6'
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className='w-full'>
          <Label htmlFor="name">Name</Label>
          <Input
            id='name'
            {...register('name', { required: 'Name is required' })}
            className="mt-1"
          />
          {errors.name && (
            <p className='mt-1 text-sm text-destructive' role='alert'>{errors.name.message}</p>
          )}
        </div>

        <div className='w-full'>
          <Label htmlFor='organizationType'>Organization type</Label>
          <Controller
            name="organizationType"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger id='organizationType' className="mt-1 w-full">
                  <SelectValue placeholder="Select answer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='PLACEHOLDER_SELECT_ANSWER' disabled>Select answer</SelectItem>
                  <SelectItem value='SCHOOL_K12'>School (K12)</SelectItem>
                  <SelectItem value='UNIVERSITY'>University</SelectItem>
                  <SelectItem value='ONLINE_SCHOOL'>Online school</SelectItem>
                  <SelectItem value='MICROSCHOOL'>Microschool</SelectItem>
                  <SelectItem value='HOMESCHOOL'>Homeschool</SelectItem>
                  <SelectItem value='TUTOR_PROGRAMME'>Tutor programme</SelectItem>
                  <SelectItem value='NGO'>NGO</SelectItem>
                  <SelectItem value='OTHER'>Other</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          {errors.organizationType && (
            <p className='mt-1 text-sm text-destructive' role='alert'>{errors.organizationType.message}</p>
          )}
        </div>

        <div className='w-full'>
          <Label htmlFor='moderationPolicy'>Moderation policy</Label>
          <Controller
            name="moderationPolicy"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger id='moderationPolicy' className="mt-1 w-full">
                  <SelectValue placeholder="Select policy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='STRICT'>Strict</SelectItem>
                  <SelectItem value='MODERATE'>Moderate</SelectItem>
                  <SelectItem value='LENIENT'>Lenient</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className='w-full'>
          <Label htmlFor='verificationStatus'>Verification status</Label>
          <Controller
            name="verificationStatus"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger id='verificationStatus' className="mt-1 w-full">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='PENDING'>Pending</SelectItem>
                  <SelectItem value='VERIFIED'>Verified</SelectItem>
                  <SelectItem value='REJECTED'>Rejected</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className='w-full'>
          <Label htmlFor='accountStatus'>Account status</Label>
          <Controller
            name="accountStatus"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger id='accountStatus' className="mt-1 w-full">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='_EMPTY_STRING_VALUE_'>None</SelectItem>
                  <SelectItem value='ACTIVE'>Active</SelectItem>
                  <SelectItem value='TRIAL'>Trial</SelectItem>
                  <SelectItem value='DORMANT'>Dormant</SelectItem>
                  <SelectItem value='TEST_DATA'>Test Data</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className='flex items-center space-x-2'>
          <Controller
            name="privateStudentChatsEnabled"
            control={control}
            render={({ field }) => (
              <Switch
                id="privateStudentChatsEnabled"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <div className='grid gap-1.5 leading-none'>
            <Label htmlFor='privateStudentChatsEnabled' className='text-sm font-medium'>Private student chats</Label>
            <p className='text-sm text-muted-foreground'>This setting makes student chats private and only allows educators to view summarised chat information.</p>
          </div>
        </div>

        <div className='ml-auto flex items-center'>
          {isSuccess && (
            <Check className='mr-2 h-5 w-5 text-green-500' />
          )}
          <Button
            className='w-fit'
            disabled={isLoading}
            type='submit'
            loading={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default Settings
